/**
 * WageProAuthLayout — split-pane chrome for the pre-auth `/signup` and
 * `/login` screens (WagePro signup epic — restyle pass added per direct
 * user request, matching Ledger's own login page's visual archetype).
 *
 * A thin wrapper around the shared `<AuthShell>` from `@jumpstone/ui-react`
 * (`packages/jumpstone-ui-react/src/auth/auth_shell.tsx`), mirroring
 * `apps/ledger-web/src/components/layout/auth_layout.tsx`'s role: this file
 * supplies only the WagePro-specific content (logo, headline, bullet list,
 * locale switcher) — the two-column layout mechanics live in the shared
 * component. `AuthShell`'s brand-panel gradient reads `var(--accent)`/
 * `var(--accent-deep)`, which `apps/wagepro-web/src/styles/wagepro-theme.css`
 * already re-points to WagePro's green — no per-product fork needed.
 *
 * NOTE (found while wiring this up): `apps/ledger-web`'s own `AuthLayout`
 * does NOT actually render `<AuthShell>` today, despite its docblock
 * claiming otherwise — it hand-rolls an equivalent-but-not-identical layout.
 * This file is the first real consumer of `<AuthShell>` in the live app
 * tree.
 */
import { useTranslation } from '@mukil/i18n';
import { AuthShell } from '@jumpstone/ui-react';
import { WAGEPRO_LOCALES } from '../../lib/i18n.js';
import type { WageProLocale } from '../../lib/i18n.js';

interface WageProAuthLayoutProps {
  children: React.ReactNode;
}

/** Same endonym labels/name keys as `LocaleSwitcher` (`components/common/locale_switcher.tsx`) — not reused directly because that component's `<Button variant="ghost">` treatment is styled for the app's light-surface header, not this dark accent-gradient panel; the segmented-pill visual here has no design-system equivalent to extend, so it's a small, contained, product-specific composition (matches this file's existing bullet-list `<ul>`, same reasoning). */
const LOCALE_LABELS: Record<WageProLocale, string> = {
  'en-001': 'EN',
  'si-LK': 'සිං',
  'ta-LK': 'தமிழ்',
};

const LOCALE_NAME_KEYS: Record<WageProLocale, string> = {
  'en-001': 'locale.english',
  'si-LK': 'locale.sinhala',
  'ta-LK': 'locale.tamil',
};

/** Pill-style language switcher for the auth brand panel — "Language" label + a rounded segmented control, active locale as a solid white chip. */
function AuthPanelLocaleSwitcher(): React.JSX.Element {
  const { t, locale, setLocale } = useTranslation('wp');

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-white/60">
        {t('auth_panel.language_label')}
      </span>
      <div
        className="flex items-center gap-0.5 rounded-full bg-white/10 p-1"
        role="group"
        aria-label={t('locale.switcher')}
      >
        {WAGEPRO_LOCALES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={locale === candidate}
            aria-label={t(LOCALE_NAME_KEYS[candidate])}
            onClick={() => {
              setLocale(candidate);
            }}
            className={
              locale === candidate
                ? 'rounded-full bg-white px-3 py-1 text-xs font-semibold text-[color:var(--accent-deep)]'
                : 'rounded-full px-3 py-1 text-xs font-medium text-white/70 transition-colors hover:text-white'
            }
          >
            {LOCALE_LABELS[candidate]}
          </button>
        ))}
      </div>
    </div>
  );
}

export function WageProAuthLayout({
  children,
}: WageProAuthLayoutProps): React.JSX.Element {
  const { t } = useTranslation('wp');

  const logo = (
    <>
      <img
        src="/brand/logo-mark-dark.svg"
        alt={t('brand.logo_alt')}
        className="h-8 w-8"
      />
      {/* eslint-disable-next-line ledger/no-untranslated-jsx-text -- brand name */}
      <span className="text-xl font-semibold tracking-tight text-white">
        WagePro
      </span>
    </>
  );

  const bullets = [
    t('auth_panel.bullet_pay_runs'),
    t('auth_panel.bullet_filings'),
    t('auth_panel.bullet_payslips'),
  ];

  const subtitle = (
    <ul className="mt-5 space-y-2.5">
      {bullets.map((item) => (
        <li
          key={item}
          className="flex items-start gap-2.5 text-sm text-white/70"
        >
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/50" />
          {item}
        </li>
      ))}
    </ul>
  );

  return (
    <AuthShell
      logo={logo}
      tagline={t('auth_panel.headline')}
      subtitle={subtitle}
      brandFooter={<AuthPanelLocaleSwitcher />}
    >
      {children}
    </AuthShell>
  );
}
